import React, {useEffect, useState} from "react";
import "./topbar.css";
import {
    NotificationsNone,
    Settings,
    Logout,
    Person,
    Dashboard,
    Menu as MenuIcon
} from "@mui/icons-material";
import {
    Avatar,
    Menu,
    MenuItem,
    useMediaQuery,
    Divider,
    ListItemIcon,
    Box,
    Tooltip,
    IconButton,
    Typography,
    Badge,
    AppBar,
    Toolbar,
    Container,
    useTheme,
    alpha
} from "@mui/material";
import {Link} from "react-router-dom";
import CryptoJS from "crypto-js";
import {ApiDetails, KEY} from "../../dummyData";

export default function Topbar({ onMenuToggle }) {
    const [forceRefresh, setForceRefresh] = useState(false);
    const [isAgent, setIsAgent] = useState(false);
    const [landlordImage, setLandlordImage] = useState("");
    const [name, setName] = useState("New User");
    const [anchorEl, setAnchorEl] = useState(null);
    const matches = useMediaQuery("(max-width:700px)");
    const open = Boolean(anchorEl);
    const theme = useTheme();

    // Helper to extract initials from a name
    const getInitials = (fullName) =>
        fullName
            .split(" ")
            .map((part) => part[0]?.toUpperCase())
            .join("");

    // Fetch user and image details
    const fetchUserData = async (decryptedData) => {
        const userDetails = JSON.parse(decryptedData);
        const [firstName, lastName, email, userType] = [
            userDetails[1][1],
            userDetails[2][1],
            userDetails[5][1],
            userDetails[21][1],
        ];

        setName(`${firstName} ${lastName}`);

        // Fetch landlord image
        try {
            const response = await fetch(`${ApiDetails}pegasus/visionary/authorization/photo`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({email_address: email}),
            });
            const data = await response.json();
            if (response.status === 200 && data.landlord_photo?.base64 !== "No File Found") {
                setLandlordImage(`data:image/jpeg;base64,${data.landlord_photo.base64}`);
            }
        } catch (error) {
            console.error("Error fetching landlord image:", error);
        }

        // Fetch user type
        try {
            const response = await fetch(`${ApiDetails}pegasus/visionary/extras/user_types`);
            const data = await response.json();
            if (response.status === 200) {
                const agentTypeId = data.find((type) => type.description === "AGENT")?.id;
                setIsAgent(userType === agentTypeId);
            }
        } catch (error) {
            console.error("Error fetching user type:", error);
        }
    };

    // Initial data load
    useEffect(() => {
        try {
            const encryptedData = localStorage.getItem("myMap");
            if (encryptedData) {
                const decryptedData = CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8);
                fetchUserData(decryptedData);
            }
        } catch (error) {
            console.error("Error decrypting user data:", error);
        }
    }, [forceRefresh, matches]);

    // Handlers
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    return {
        setForceRefresh,
        render: (
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    color: 'text.primary',
                    zIndex: theme.zIndex.drawer + 1,
                    width: { xs: '100%', sm: `calc(100% - ${250}px)` },
                    ml: { xs: 0, sm: `${250}px` },
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}
            >
                <Container maxWidth={false}>
                    <Toolbar disableGutters sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 1
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {matches && (
                                <IconButton
                                    color="inherit"
                                    aria-label="open drawer"
                                    edge="start"
                                    onClick={onMenuToggle}
                                    sx={{ mr: 2, display: { sm: 'none' } }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    fontWeight: 600,
                                    fontSize: matches ? '0.9rem' : '1.1rem',
                                    color: '#2c3e50',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Welcome, {name.toUpperCase()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Account settings">
                                <IconButton
                                    onClick={handleClick}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                        }
                                    }}
                                >
                                    {landlordImage ? (
                                        <Avatar
                                            src={landlordImage}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                border: '2px solid',
                                                borderColor: '#e0e6ed',
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                                color: 'white',
                                                fontWeight: 700,
                                                border: '2px solid',
                                                borderColor: '#e0e6ed',
                                                fontSize: '1rem'
                                            }}
                                        >
                                            {getInitials(name)}
                                        </Avatar>
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            transformOrigin={{ horizontal: "right", vertical: "top" }}
                            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                            PaperProps={{
                                elevation: 3,
                                sx: {
                                    mt: 1.5,
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                                    '&:before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                },
                            }}
                        >
                            <MenuItem
                                sx={{
                                    py: 1.5,
                                    px: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5
                                }}
                            >
                                <Avatar
                                    src={landlordImage}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        background: landlordImage ? 'transparent' : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                    }}
                                >
                                    {!landlordImage && getInitials(name)}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                        {isAgent ? 'Agent Account' : 'User Account'}
                                    </Typography>
                                </Box>
                            </MenuItem>

                            <Divider />

                            {isAgent && (
                                <MenuItem
                                    onClick={handleClose}
                                    component={Link}
                                    to="/userManagement"
                                    sx={{
                                        py: 1,
                                        color: 'text.primary',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.light, 0.1)
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <Person fontSize="small" color="primary" />
                                    </ListItemIcon>
                                    User Management
                                </MenuItem>
                            )}

                            <MenuItem
                                onClick={handleClose}
                                component={Link}
                                to="/settings"
                                sx={{
                                    py: 1,
                                    color: 'text.primary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.light, 0.1)
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <Dashboard fontSize="small" color="primary" />
                                </ListItemIcon>
                                My Account
                            </MenuItem>

                            <Divider />

                            <MenuItem
                                onClick={handleClose}
                                component={Link}
                                to="/settings"
                                sx={{
                                    py: 1,
                                    color: 'text.primary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.light, 0.1)
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <Settings fontSize="small" color="primary" />
                                </ListItemIcon>
                                Settings
                            </MenuItem>

                            <MenuItem
                                onClick={handleLogout}
                                sx={{
                                    py: 1,
                                    color: 'text.primary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.error.light, 0.1)
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <Logout fontSize="small" color="error" />
                                </ListItemIcon>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </Container>
            </AppBar>
        )
    };
}